import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'

import { getActionbySecurityCheckFamily } from '../../redux/actions/actionActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import {
  getSecurityControlById,
  updateSecuritycontrols,
} from '../../redux/securityControls/securityControlsAction'
import { selectSecurityControl } from '../../redux/securityControls/securityControlsSelector'
import {
  fetchDocumentById,
  fetchDocumentsByFolder,
} from '../../redux/documents/documentAction'
import {
  selectDocument,
  selectDocuments,
} from '../../redux/documents/documentSelector'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { SecurityControls } from '../../types/securityControls'
import { formatTitle } from '../../utils/formatTitle'
import { formatStructuredDescription } from '../../utils/formatStructuredDescription'
import DocumentSelector from '../../components/common/DocumentSelector'
import ConfirmDialog from '../../components/containers/ConfirmDialog'
import UseAlert from '../../components/common/alert'
import {
  DetailAccordion,
  DetailButton,
  DetailDescriptionPanel,
  DetailKpiCard,
  DetailKpiGrid,
  DetailPageHead,
  RelatedActionsPanel,
} from '../../components/common/detail'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import '../../components/common/ListPageTable.css'
import { formatDecimal } from '../../utils/formatNumbers'

export default function ActionListBySecurityCheckFamily() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()

  const actionsList = useSelector(
    (state: RootState) => state.action.actionsbySecurity
  )
  const securityControl = useSelector(selectSecurityControl)
  const loading = useSelector((state: RootState) => state.action.loading)
  const showLoader = useTableLoadingGate(loading, [id])
  const roles = useSelector((state: RootState) => state.auth.roles)
  const document = useSelector(selectDocument)
  const existingDocuments = useSelector(selectDocuments)

  const [open, setOpen] = useState({
    document: true,
    actions: true,
  })
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (id) {
      dispatch(getSecurityControlById(id))
      dispatch(getActionbySecurityCheckFamily(id))
      dispatch(fetchDocumentsByFolder('securityControls'))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (securityControl?.file && typeof securityControl.file === 'string') {
      dispatch(fetchDocumentById(securityControl.file))
    }
  }, [dispatch, securityControl?.file])

  const handleFileUpdate = async (newFile: File | string | null) => {
    if (newFile === null) {
      setDeleteConfirmOpen(true)
      return
    }

    if (!id) return

    try {
      const updatedControl = {
        _id: id,
        file: newFile,
      } as unknown as SecurityControls

      await dispatch(
        updateSecuritycontrols({
          updatedSecurityControl: updatedControl,
          _id: id,
        })
      ).unwrap()
      dispatch(getSecurityControlById(id))
      setAlert({
        open: true,
        message: t('alerts.fileUpdateSuccess'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.fileUpdateError'),
        severity: 'error',
      })
    }
  }

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false)
    if (!id) return

    try {
      const updatedControl = {
        _id: id,
        file: null,
      } as unknown as SecurityControls

      await dispatch(
        updateSecuritycontrols({
          updatedSecurityControl: updatedControl,
          _id: id,
        })
      ).unwrap()
      dispatch(getSecurityControlById(id))
      setAlert({
        open: true,
        message: t('alerts.fileDeleteSuccess'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.fileDeleteError'),
        severity: 'error',
      })
    }
  }

  const rows = Array.isArray(actionsList) ? actionsList : []
  const title =
    formatTitle(securityControl?.Label as string) ||
    t('Security Control', { defaultValue: 'Security control' })
  const score = Number(securityControl?.SCScore)
  const scoreLabel = Number.isFinite(score) ? formatDecimal(score) : '—'
  const source = securityControl?.Source
    ? formatTitle(securityControl.Source)
    : '—'
  const hasFile = Boolean(securityControl?.file)
  const formattedDescription = formatStructuredDescription(
    securityControl?.Description
  )
  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((current) => ({ ...current, open: false }))}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('securityControlDetailsPage.eyebrow')}
          title={title}
          subtitle={t('securityControlDetailsPage.subtitle')}
          actions={
            canAnalyze && id ? (
              <DetailButton
                onClick={() =>
                  navigate(`/security-control-details/view-analysis/${id}`)
                }
              >
                <BarChartOutlined sx={{ fontSize: 16 }} />
                {t('buttons.viewAnalysis')}
              </DetailButton>
            ) : null
          }
        />

        <DetailKpiGrid columns={4}>
          <DetailKpiCard
            label={t('controleSecurityDetails.accordion.score')}
            value={scoreLabel}
          />
          <DetailKpiCard
            label={t('controleSecurityDetails.accordion.source')}
            value={source}
            valueSize="sm"
          />
          <DetailKpiCard
            label={t('securityControlDetailsPage.relatedTasks')}
            value={rows.length}
          />
          <DetailKpiCard
            label={t('securityControlDetailsPage.fileAttached')}
            value={
              hasFile
                ? t('Yes', { defaultValue: 'Yes' })
                : t('No', { defaultValue: 'No' })
            }
            valueSize="sm"
          />
        </DetailKpiGrid>

        <DetailDescriptionPanel
          title={t('Description')}
          subtitle={t('securityControlDetailsPage.descriptionSubtitle')}
          text={formattedDescription}
          emptyLabel={t('securityControlDetailsPage.noDescription')}
        />

        <DetailAccordion
          title={t('SecurityControls.politiqueProcedure', {
            defaultValue: 'Policy and procedure',
          })}
          open={open.document}
          onToggle={() =>
            setOpen((current) => ({
              ...current,
              document: !current.document,
            }))
          }
        >
          <div className="detail-acc-inner">
            <DocumentSelector
              label={t('buttons.uploadPolitique', {
                defaultValue: 'Upload policy and procedure',
              })}
              file={securityControl?.file || null}
              displayFileName={document?.originalName}
              previewUrl={document?.path}
              onFileChange={handleFileUpdate}
              existingFiles={
                existingDocuments?.map((doc) => ({
                  name: doc.originalName,
                  url: doc.path,
                })) || []
              }
              allowSelectExisting
            />
          </div>
        </DetailAccordion>

        <RelatedActionsPanel
          title={t('Tasks', { defaultValue: 'Corrective actions' })}
          listTitle={t('tasks_list', { defaultValue: 'Task list' })}
          emptyMessage={t('securityControlDetailsPage.empty.actions')}
          rows={rows}
          loading={showLoader}
          loadingDeps={[id]}
          roles={roles}
          open={open.actions}
          onToggle={() =>
            setOpen((current) => ({
              ...current,
              actions: !current.actions,
            }))
          }
        />
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        tone="danger"
        title={t('alerts.deleteFileConfirmTitle')}
        message={t('alerts.deleteFileConfirm')}
        confirmText={t('buttons.delete', { defaultValue: 'Delete' })}
        cancelText={t('buttons.cancel', { defaultValue: 'Cancel' })}
      />
    </>
  )
}
